import React from 'react';
import { Link } from 'gatsby';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Column } from '@dls/web';
import { GatsbyImage as Img } from 'gatsby-plugin-image';
import { Caption, H5, SubtitleSecondary } from '../Base';

import slugify from '../../helpers/slugify';
import formatPrice from '../../helpers/formatPrice';

import useTranslation from '../../hooks/useTranslation';

import PersistStatusConsumer from '../PersistStatusConsumer';
import { getRibbon } from '../../helpers/catalogue-helpers';

const Card = styled(props => <Link {...props} />)`
  display: block;
  flex-grow: 1;
  color: ${p => p.theme.colours.nearBlack};
  background-color: ${p => p.theme.colours.white};
  padding: ${p => p.theme.spacing(3)} ${p => p.theme.spacing(2)};
  text-align: center;

  ${p => p.theme.media.md`
    padding: ${p.theme.spacing(5)} ${p.theme.spacing(2)};
  `};
`;

const ColumnCentered = styled(props => <Column {...props} />)`
  max-width: ${p => p.theme.spacing(25)};
  margin: 0 auto;
`;

const StyledTitle = styled(H5)`
  margin-bottom: ${p => p.theme.spacing(3)};
  height: ${p => p.theme.spacing(6)};
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const StyledHeroImage = styled(props => <Img {...props} />)`
  margin-bottom: ${p => p.theme.spacing(3)};
  ${p => p.theme.media.md`
    margin-bottom: ${p.theme.spacing(4)};
  `};
`;

const StyledRibbon = styled(Caption)`
  color: ${p => p.theme.colours.primaryVariant};
  margin-bottom: ${p => p.theme.spacing(1)};
  height: ${p => p.theme.spacing(4)};
`;

const PhoneCard = ({
  heroImage,
  installmentPrice,
  monthlyTerm,
  partialUpfrontAmount,
  title,
  publicPrice,
  slug,
  ribbon,
  selectedPlan
}) => {
  const { t } = useTranslation();
  // Default price text
  let priceText = t('DEFAULT_PRICE_TEXT', [formatPrice(publicPrice, 0)]);

  const ribbonForPlan = getRibbon(ribbon, selectedPlan);
  // Installment price text with monthly term duration
  let installmentPriceText = t('INSTALLMENT_PRICE_TEXT', [
    formatPrice(installmentPrice),
    monthlyTerm
  ]);

  if (partialUpfrontAmount >= 0 && installmentPrice >= 0) {
    priceText = installmentPriceText;
  }

  if (!partialUpfrontAmount && installmentPrice >= 0) {
    priceText = installmentPriceText;
  }

  return (
    <Card
      to={`/phones/${slug}`}
      state={{
        installTerm: monthlyTerm
      }}
    >
      <ColumnCentered sm={12}>
        {heroImage && (
          <StyledHeroImage
            image={heroImage.childImageSharp.gatsbyImageData}
            alt={title}
          />
        )}
      </ColumnCentered>
      <ColumnCentered sm={12}>
        <StyledRibbon> {ribbonForPlan?.title || ''}</StyledRibbon>
      </ColumnCentered>
      <Column sm={12}>
        <StyledTitle>{title}</StyledTitle>
      </Column>
      <Column sm={12}>
        <PersistStatusConsumer>
          <SubtitleSecondary data-testid={`price-${slugify(title)}`}>
            {priceText}
          </SubtitleSecondary>
        </PersistStatusConsumer>
      </Column>
    </Card>
  );
};

PhoneCard.propTypes = {
  heroImage: PropTypes.object,
  installmentPrice: PropTypes.number,
  monthlyTerm: PropTypes.string,
  partialUpfrontAmount: PropTypes.number,
  title: PropTypes.string,
  publicPrice: PropTypes.number,
  slug: PropTypes.string,
  ribbon: PropTypes.string
};

export default PhoneCard;
